/**
 * Névoa com a visão dos tokens (`docs/spec/scene-walls.md`):
 * - `useSightState` monta o que decide o que uma pessoa vê (os cones dos tokens que dão visão a
 *   ela, as luzes, a luz ambiente), com cache por token: só recalcula o token que mudou;
 * - `VisionFog` desenha a névoa por máscaras SVG. As interseções com a luz saem de máscaras
 *   aninhadas: "luz intensa ∩ alcance de luz intensa" é o caminho do alcance com a máscara da luz.
 *   Opaco onde não se vê nada; esmaecido onde há memória, zona parcialmente visível ou alcance
 *   intenso só com luz fraca; limpo no que se vê agora. As zonas fixas mandam por último.
 *   O que se vê só pela visão no escuro (o alcance de escuro, onde nenhuma luz deixa ver) aparece
 *   sem cor e mais escuro (BRAZ-23), para a pessoa saber que enxerga no escuro, não com luz.
 */
import { useRef } from 'react';
import type { SceneModel, SceneToken } from './model.js';
import {
  ZONE,
  lightShapes,
  lightTokens,
  polysPath,
  rectsPath,
  segmentsPath,
  toCells,
  tokenCenter,
  tokenSight,
  visionTokens,
  wallHits,
  zoneRects,
  type Bounds,
  type LightShapes,
  type Shape,
  type SightSeg,
  type SightState,
  type TokenSightShapes,
  type Viewer,
} from './vision.js';

/** Posição a usar para cada token (o arrastado vai na posição do arrasto). */
export type TokenPos = (t: SceneToken) => { x: number; y: number };

/** Assinatura das paredes (o que muda a visão e o movimento). */
export const wallsKey = (scene: SceneModel) => scene.walls.map((w) => `${w.id}:${w.points.join(',')}:${w.closed}:${w.sight}:${w.move}:${w.dir}:${w.open}`).join('|');

/** O estado de visão de quem olha, com cache dos cones por token (`segs`: `sightSegments` das paredes). */
export function useSightState(scene: SceneModel, viewer: Viewer | null, pos: TokenPos, bounds: Bounds, segs: SightSeg[]): SightState | null {
  const cache = useRef<{ segs: SightSeg[] | null; key: string; map: Map<string, TokenSightShapes | LightShapes> }>({ segs: null, key: '', map: new Map() });
  if (!viewer) return null;
  const boundsKey = `${bounds.x},${bounds.y},${bounds.w},${bounds.h}`;
  if (cache.current.segs !== segs || cache.current.key !== boundsKey) cache.current = { segs, key: boundsKey, map: new Map() };
  const map = cache.current.map;
  const next = new Map<string, TokenSightShapes | LightShapes>();
  const cd = scene.cellDistance;
  const cone = (t: SceneToken, angle: number) => (angle < 360 ? { facing: t.facing, angle } : undefined);

  const tokens = visionTokens(scene.tokens, viewer, scene.fogSharing).map((t) => {
    const c = tokenCenter({ ...pos(t), size: t.size });
    const ranges = { bright: toCells(t.sight.bright, cd), dim: toCells(t.sight.dim, cd), dark: toCells(t.sight.dark, cd) };
    const k = `v:${t.id}:${c.x},${c.y}:${ranges.bright},${ranges.dim},${ranges.dark}:${t.sight.angle}:${t.facing}`;
    const shapes = (map.get(k) as TokenSightShapes | undefined) ?? tokenSight(t.id, c.x, c.y, ranges, segs, bounds, cone(t, t.sight.angle));
    next.set(k, shapes);
    return shapes;
  });
  const lights = lightTokens(scene.tokens).map((t) => {
    const c = tokenCenter({ ...pos(t), size: t.size });
    const ranges = { bright: toCells(t.sight.lightBright, cd), dim: toCells(t.sight.lightDim, cd) };
    const k = `l:${t.id}:${c.x},${c.y}:${ranges.bright},${ranges.dim}:${t.sight.lightAngle}:${t.facing}`;
    const shapes = (map.get(k) as LightShapes | undefined) ?? lightShapes(t.id, c.x, c.y, ranges, segs, bounds, cone(t, t.sight.lightAngle));
    next.set(k, shapes);
    return shapes;
  });
  cache.current.map = next;
  return { ambient: scene.ambientLight, tokens, lights };
}

const pts = (list: (Shape | null)[]) => list.filter((s): s is Shape => !!s).map((s) => s.points);

/** Assinatura curta de um texto (FNV-1a), para pôr o conteúdo de uma máscara no id dela. */
function signature(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) h = Math.imul(h ^ text.charCodeAt(i), 0x01000193);
  return (h >>> 0).toString(36);
}

export function VisionFog({
  st,
  scene,
  zones,
  g,
  area,
  ids,
  gmView,
  memoryPath,
  memoryCount,
}: {
  st: SightState;
  scene: SceneModel;
  /** as zonas por célula (`zoneGrid`) */
  zones: Uint8Array;
  g: number;
  /** o que a névoa cobre, em px */
  area: { x: number; y: number; x2: number; y2: number };
  /** prefixo único dos ids das máscaras */
  ids: string;
  /** mestre olhando como mestre: tudo translúcido, sem a faixa das paredes */
  gmView: boolean;
  /** memória dos tokens (px), já pronta, e quantas meias células ela tem */
  memoryPath: string;
  memoryCount: number;
}) {
  const tb = polysPath(pts(st.tokens.map((t) => t.bright)), g);
  const td = polysPath(pts(st.tokens.map((t) => t.dim)), g);
  const tk = polysPath(pts(st.tokens.map((t) => t.dark)), g);
  const lb = polysPath(pts(st.lights.map((l) => l.bright)), g);
  const ld = polysPath(pts(st.lights.map((l) => l.dim)), g);
  const zone = (kind: number) => rectsPath(zoneRects(zones, scene.cols, scene.rows, kind), g);
  const reveal = zone(ZONE.reveal);
  const hide = zone(ZONE.hide);
  const semiRects = zoneRects(zones, scene.cols, scene.rows, ZONE.semi);
  const semi = rectsPath(semiRects, g);
  const semiCells = semiRects.reduce((n, r) => n + r.w * r.h, 0);
  // as paredes em que a visão encosta (cada alcance com a luz que vale para ele)
  const hits = (pick: (t: TokenSightShapes) => Shape | null) => segmentsPath(st.tokens.flatMap((t) => (pick(t) ? wallHits(pick(t)!) : [])), g);
  const [hb, hd, hk] = [hits((t) => t.bright), hits((t) => t.dim), hits((t) => t.dark)];

  const rect = { x: area.x, y: area.y, width: area.x2 - area.x, height: area.y2 - area.y };
  // as máscaras da luz são usadas dentro das outras, e o Chromium não redesenha a de fora quando só a
  // de dentro muda: a névoa ficava com a luz velha até recarregar (BRAZ-27). Com o conteúdo no id, a
  // luz que muda troca a referência dentro das outras, e elas são redesenhadas
  const light = signature(`${st.ambient}#${lb}#${ld}#${rect.x},${rect.y},${rect.width},${rect.height}`);
  const litB = `${ids}-litb-${light}`;
  const litD = `${ids}-litd-${light}`;
  const opaque = `${ids}-opaque`;
  const dim = `${ids}-dim`;
  const darkOnly = `${ids}-darkvision`;
  const soft = `${ids}-soft-${signature(String(g))}`;
  const mask = (id: string) => `url(#${id})`;
  const band = Math.max(1, g * 0.1);

  return (
    <g className="rd-vision" pointerEvents="none" data-memory={memoryCount} data-semi={semiCells}>
      <defs>
        {/* a memória é guardada em meias células: o desfoque tira os degraus da borda (o original também suaviza) */}
        <filter id={soft} x="-5%" y="-5%" width="110%" height="110%">
          <feGaussianBlur stdDeviation={g * 0.12} />
        </filter>
        <mask id={litB} maskUnits="userSpaceOnUse" {...rect}>
          <rect {...rect} fill={st.ambient === 'intense' ? 'white' : 'black'} />
          {st.ambient !== 'intense' && lb ? <path d={lb} fill="white" /> : null}
        </mask>
        <mask id={litD} maskUnits="userSpaceOnUse" {...rect}>
          <rect {...rect} fill={st.ambient === 'weak' ? 'white' : 'black'} />
          {st.ambient !== 'weak' && ld ? <path d={ld} fill="white" /> : null}
        </mask>
        <mask id={opaque} maskUnits="userSpaceOnUse" {...rect}>
          <rect {...rect} fill="white" />
          {tb ? <path d={tb} fill="black" mask={mask(litB)} /> : null}
          {td ? <path d={td} fill="black" mask={mask(litD)} /> : null}
          {tk ? <path d={tk} fill="black" /> : null}
          {tb ? <path d={tb} fill="black" mask={mask(litD)} /> : null}
          {memoryPath ? <path d={memoryPath} fill="black" filter={`url(#${soft})`} /> : null}
          {semi ? <path d={semi} fill="black" /> : null}
          {reveal ? <path d={reveal} fill="black" /> : null}
          {hide ? <path d={hide} fill="white" /> : null}
        </mask>
        <mask id={dim} maskUnits="userSpaceOnUse" {...rect}>
          <rect {...rect} fill="black" />
          {memoryPath ? <path d={memoryPath} fill="white" filter={`url(#${soft})`} /> : null}
          {semi ? <path d={semi} fill="white" /> : null}
          {/* o alcance de escuro vê agora: a memória ali dá lugar à visão no escuro; a luz fraca vista
              pelo alcance intenso continua esmaecida, para os níveis de luz não se igualarem */}
          {tk ? <path d={tk} fill="black" /> : null}
          {tb ? <path d={tb} fill="white" mask={mask(litD)} /> : null}
          {tb ? <path d={tb} fill="black" mask={mask(litB)} /> : null}
          {td ? <path d={td} fill="black" mask={mask(litD)} /> : null}
          {reveal ? <path d={reveal} fill="black" /> : null}
          {hide ? <path d={hide} fill="black" /> : null}
        </mask>
        {/* só a visão no escuro: o alcance de escuro menos o que alguma luz deixa ver */}
        <mask id={darkOnly} maskUnits="userSpaceOnUse" {...rect}>
          <rect {...rect} fill="black" />
          {tk ? <path d={tk} fill="white" /> : null}
          {tb ? <path d={tb} fill="black" mask={mask(litB)} /> : null}
          {tb ? <path d={tb} fill="black" mask={mask(litD)} /> : null}
          {td ? <path d={td} fill="black" mask={mask(litD)} /> : null}
          {reveal ? <path d={reveal} fill="black" /> : null}
          {hide ? <path d={hide} fill="black" /> : null}
        </mask>
      </defs>
      {/* visão no escuro: tira a cor (mesclagem "saturation" com cinza) e escurece. Cada retângulo leva
          a própria máscara, porque uma máscara no grupo isolaria a mesclagem do mapa embaixo */}
      {!gmView && tk ? (
        <>
          <rect className="rd-scene__darkvision" data-vision-fog="darkvision" {...rect} mask={mask(darkOnly)} />
          <rect className="rd-scene__darkvisionShade" {...rect} mask={mask(darkOnly)} />
        </>
      ) : null}
      <rect className={`rd-scene__fog${gmView ? ' rd-scene__fog--gm' : ''}`} data-vision-fog="opaque" {...rect} mask={mask(opaque)} />
      <rect className={`rd-scene__fogDim${gmView ? ' rd-scene__fog--gm' : ''}`} data-vision-fog="dim" {...rect} mask={mask(dim)} />
      {!gmView ? (
        <g className="rd-vision__walls" fill="none" strokeWidth={band} strokeLinecap="round">
          {hb ? <path d={hb} mask={mask(litB)} /> : null}
          {hd ? <path d={hd} mask={mask(litD)} /> : null}
          {hk ? <path d={hk} /> : null}
        </g>
      ) : null}
    </g>
  );
}

const area = (p: number[]) => {
  let a = 0;
  for (let i = 0, j = p.length - 2; i < p.length; j = i, i += 2) a += (p[j]! + p[i]!) * (p[j + 1]! - p[i + 1]!);
  return Math.abs(a / 2);
};

/** Contorno da visão possível de um token (o selecionado): o maior dos alcances, como um contorno só. */
export function SightOutline({ shapes, g, zoom, gmView }: { shapes: TokenSightShapes; g: number; zoom: number; gmView: boolean }) {
  const largest = pts([shapes.bright, shapes.dim, shapes.dark]).sort((p, q) => area(q) - area(p))[0];
  const d = largest ? polysPath([largest], g) : '';
  if (!d) return null;
  return <path className="rd-vision__outline" d={d} fill="none" stroke={gmView ? '#ffff00' : '#000000'} strokeOpacity={0.8} strokeWidth={2 / zoom} pointerEvents="none" />;
}
